import React from 'react'
import PropTypes from 'prop-types'
import { GeneralTitle } from '../Shared'
import CustomList from '../Shared/CustomList'
import { Card, Collapse, List, Space, Steps, Tabs, Timeline } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import CustomButton from '../Shared/CustomButton'
import VideoPlaceholder from '../Templates/VideoPlaceholder'
import ImgPlaceholder from '../Templates/ImgPlaceholder'
import InRow from './InRow'

/**
 * A single "array-style" prop in this component always follows this pattern:
 *
 * [content, wrapperProps?, itemProps?]
 *
 * - content     → what to render (string, array, objects, React nodes)
 * - wrapperProps → props passed to the top-level wrapper for that section (e.g. <p>, <CustomList>, <InRow>, <Tabs>, <Timeline>)
 * - itemProps    → props applied to each rendered item (bullets, cards, tabs children), or an array of props (for cards)
 */

/**
 * @typedef {Object} PresentationTitle
 * @property {string} 0 Title text
 * @property {Object} [1] Props for <GeneralTitle> (e.g. { style, className })
 * @property {Object} [2] (unused for title)
 */

/**
 * @typedef {Object} PresentationDescription
 * @property {string | string[]} 0 Description text; can be string or array of paragraphs
 * @property {Object} [1] Props for <p> (applied to every paragraph)
 * @property {Object} [2] (unused)
 */

/**
 * Bullets section
 *
 * content: array of strings (or nodes)
 * wrapperProps: props for <CustomList>
 * itemProps: props for each <List.Item>, plus optional iconProps
 *
 * iconProps can contain:
 * - icon: React component (e.g. CheckCircleOutlined)
 * - ...rest: passed to icon
 *
 * @example
 * bullets={[
 *   ['Fast install', 'Works with any theme'],
 *   { style: { marginTop: 12 } },
 *   {
 *     iconProps: { style: { color: '#52c41a' } },
 *     style: { alignItems: 'flex-start' }
 *   }
 * ]}
 *
 * → renders a list with green check icons
 */

/**
 * @typedef {[
 *   string[] | React.ReactNode[],
 *   Object?,
 *   {
 *     iconProps?: { icon?: React.ComponentType<any> } & Object,
 *     [key: string]: any
 *   }?
 * ]} PresentationBullets
 */

/**
 * Cards section
 *
 * content: array of cards; each card can be:
 *  - string → rendered as text inside <Card>
 *  - React element → rendered directly
 *  - function → called and the returned node is rendered
 *
 * wrapperProps: passed to <InRow> (layout)
 * itemProps: can be:
 *  - object → applied to every <Card>
 *  - array → itemProps[index] applied to corresponding <Card>
 *
 * @example
 * cards={[
 *   [
 *     [
 *       { title: 'Starter', description: 'Bookings only' },
 *       { title: 'Shop', description: 'Bookings + products' }
 *     ],
 *     { gap: 16 }, // <InRow> props
 *     { style: { minWidth: 220 } } // <Card> props
 *   ]
 * ]}
 */

/**
 * @typedef {[
 *   Array<
 *     string |
 *     React.ReactNode |
 *     (() => React.ReactNode)
 *   >,
 *   Object?,
 *   Object | Object[]?
 * ]} PresentationCards
 */

/**
 * Tabs section
 *
 * content: array of tab items:
 *  {
 *    key?: string,
 *    tab?: string | React.ReactNode, // label in Antd terms
 *    content?: React.ReactNode,
 *    ...any extra passed to the tab item
 *  }
 *
 * wrapperProps: passed to <Tabs>
 * itemProps: passed to the tab content wrapper (<div {...itemProps}>)
 *
 * @example
 * tabs={[
 *   [
 *     [
 *       { key: 'booking', tab: 'Booking', content: <BookingDemo /> },
 *       { key: 'shop', tab: 'Shop', content: <ShopDemo /> }
 *     ],
 *     { tabPosition: 'top' },
 *     { style: { padding: 12 } }
 *   ]
 * ]}
 */

/**
 * @typedef {[
 *   Array<{
 *     key?: string,
 *     tab?: React.ReactNode,
 *     content?: React.ReactNode,
 *     [key: string]: any
 *   }>,
 *   Object?,
 *   Object?
 * ]} PresentationTabs
 */

/**
 * Timeline section
 *
 * content: array of entries, each entry can be:
 *  - string → shown as plain text
 *  - { role?: string, text: string, color?: string, ...antdTimelineItemProps }
 *
 * wrapperProps: passed to <Timeline>
 * Special key on wrapperProps: `circleColor` → function that receives entry and returns a color
 *
 * @example
 * timeline={[
 *   [
 *     [
 *       { role: 'Customer', text: 'Can I book for Wed?' },
 *       { role: 'Assistant', text: 'Available 14:00, 15:30, 17:00' },
 *       'Confirmation email sent'
 *     ],
 *     {
 *       circleColor: (entry) => entry.role === 'Assistant' ? 'green' : 'blue',
 *       mode: 'left'
 *     }
 *   ]
 * ]}
 */

/**
 * @typedef {[
 *   Array<
 *     string |
 *     {
 *       role?: string,
 *       text: string,
 *       color?: string,
 *       [key: string]: any
 *     }
 *   >,
 *   {
 *     circleColor?: (entry: any) => string
 *   }?
 * ]} PresentationTimeline
 */

/**
 * Buttons section
 *
 * content: array of button configs:
 *  {
 *    label: string,
 *    key?: string,
 *    type?: string, // passed to CustomButton
 *    icon?: React.ReactNode,
 *    loginRequired?: Object | boolean, // if truthy → wrap in <WrapperLoginRequired>
 *    ...any other CustomButton props
 *  }
 *
 * wrapperProps: passed to <Space>
 *
 * @example
 * buttons={[
 *   [
 *     [
 *       { label: 'Start Starter', type: 'primary', href: '/subscribe' },
 *       {
 *         label: 'Download ZIP',
 *         loginRequired: { target: 'download-plugin' }
 *       }
 *     ],
 *     { size: 'middle', wrap: true }
 *   ]
 * ]}
 */

/**
 * @typedef {[
 *   Array<{
 *     label: string,
 *     key?: string,
 *     loginRequired?: any,
 *     [key: string]: any
 *   }>,
 *   Object?
 * ]} PresentationButtons
 */

/**
 * Image section
 *
 * content: image URL (string)
 * wrapperProps: passed to <img> or <ImgPlaceholder>
 * Special prop: `demo: true` → renders <ImgPlaceholder /> instead of real image
 *
 * @typedef {[
 *   string,
 *   ({
 *     demo?: boolean
 *   } & Object)?
 * ]} PresentationImage
 */

/**
 * Video section
 *
 * content: video URL (string)
 * wrapperProps: passed to <video> or <VideoPlaceholder>
 * Special prop: `demo: true` → renders <VideoPlaceholder />
 *
 * @typedef {[
 *   string,
 *   ({
 *     demo?: boolean
 *   } & Object)?
 * ]} PresentationVideo
 */

/**
 * @typedef {Object} PresentationProps
 * @property {string | PresentationTitle} [title] Title to display. If string → rendered as <GeneralTitle h="h2">string</GeneralTitle>. If array → [text, titleProps]
 * @property {string | string[] | PresentationDescription} [description] Description text. If array of strings → multiple <p>
 * @property {PresentationBullets} [bullets] Bulleted list with optional icon
 * @property {PresentationCards} [cards] Cards rendered in a row
 * @property {PresentationTabs} [tabs] Tabs rendered with Antd <Tabs />
 * @property {PresentationTimeline} [timeline] Conversation-style or steps-style timeline
 * @property {PresentationButtons} [buttons] Action buttons rendered in <Space />
 * @property {PresentationImage} [image] Image or demo placeholder
 * @property {PresentationVideo} [video] Video or demo placeholder
 * @property {Array<'title'|'description'|'image'|'video'|'bullets'|'cards'|'tabs'|'timeline'|'buttons'>} [order] Order in which sections are rendered; missing sections are skipped
 * @property {Object} [rest] Extra props passed to the root <Space>
 */

/** @param {PresentationProps} props */
const Presentation = ({
	title,
	description,
	bullets,
	buttons,
	cards,
	list,
	tabs,
	collapse,
	timeline,
	steps,
	image,
	video,
	order = [
		'title',
		'description',
		'image',
		'video',
		'bullets',
		'cards',
		'list',
		'tabs',
		'collapse',
		'timeline',
		'steps',
		'buttons'
	],
	...props
}) => {
	const activeElements = order.filter((el) => {
		switch (el) {
			case 'title':
				return Boolean(title)
			case 'description':
				return Boolean(description)
			case 'image':
				return Boolean(image)
			case 'video':
				return Boolean(video)
			case 'bullets':
				return Array.isArray(bullets) && bullets.length > 0
			case 'cards':
				return Array.isArray(cards) && cards.length > 0
			case 'list':
				return Array.isArray(list) && list.length > 0
			case 'tabs':
				return Array.isArray(tabs) && tabs.length > 0
			case 'collapse':
				return Array.isArray(collapse) && collapse.length > 0
			case 'timeline':
				return Array.isArray(timeline) && timeline.length > 0
			case 'steps':
				return Array.isArray(steps) && steps.length > 0

			case 'buttons':
				return Array.isArray(buttons) && buttons.length > 0
			default:
				return false
		}
	})

	// Set order of active elements
	const orderedElements = order.filter((el) => activeElements.includes(el))
	orderedElements.sort((a, b) => order.indexOf(a) - order.indexOf(b))

	const propsFor = (el) => {
		if (!Array.isArray(el)) {
			return {
				content: el,
				props: {},
				propsItem: {}
			}
		}
		return {
			content: el[0],
			props: el[1] || {},
			propsItem: el[2] || {}
		}
	}

	// Render elements based on orderedElements
	const renderElement = (el) => {
		switch (el) {
			case 'title':
				const { content: titleContent, props: titleProps } =
					propsFor(title)

				const { style: styleTitle, ...restTitleProps } =
					titleProps || {}
				const defaultStyleTitle = { fontSize: 30, margin: 0 }
				restTitleProps.style = { ...defaultStyleTitle, ...styleTitle }

				return (
					<GeneralTitle h="h2" {...restTitleProps}>
						{titleContent}
					</GeneralTitle>
				)
			case 'description':
				const { content: descContent, props: descProps } =
					propsFor(description)

				const { style: styleDesc, ...restDescProps } = descProps || {}
				const defaultStyleDesc = { margin: 0 }
				restDescProps.style = { ...defaultStyleDesc, ...styleDesc }

				if (Array.isArray(descContent)) {
					return descContent.map((descItem, index) => (
						<p key={`description-${index}`} {...restDescProps}>
							{descItem}
						</p>
					))
				}

				return (
					<p key="description" {...restDescProps}>
						{descContent}
					</p>
				)
			case 'image':
				const { content: imageContent, props: imageProps } =
					propsFor(image)

				const { demo: imageDemo, ...restImageProps } = imageProps || {}
				if (imageDemo) {
					return <ImgPlaceholder {...restImageProps} />
				}
				return (
					<img
						key="image"
						src={imageContent}
						alt="Presentation"
						{...restImageProps}
					/>
				)
			case 'video':
				const { content: videoContent, props: videoProps } =
					propsFor(video)
				const { demo: videoDemo, ...restVideoProps } = videoProps || {}

				if (videoDemo) {
					return <VideoPlaceholder {...restVideoProps} />
				}
				return (
					<video key="video" controls {...restVideoProps}>
						<source src={videoContent} type="video/mp4" />
						Your browser does not support the video tag.
					</video>
				)
			case 'bullets':
				const {
					content: bulletsContent,
					props: bulletsProps,
					propsItem: bulletsPropsItem
				} = propsFor(bullets)
				const hasPropsItem = Array.isArray(bulletsPropsItem)
				return (
					<CustomList
						dataSource={bulletsContent}
						renderItem={(item, index) => {
							let { iconProps = {}, ...restItemProps } =
								hasPropsItem
									? bulletsPropsItem[index]
									: bulletsPropsItem

							const { icon, style, ...restIconProps } = iconProps

							const UsedIcon = icon || CheckCircleOutlined
							return (
								<List.Item {...restItemProps}>
									<UsedIcon
										style={{
											...{
												color: '#52c41a',
												marginRight: 8
											},
											...style
										}}
										{...restIconProps}
									/>
									{item}
								</List.Item>
							)
						}}
						{...bulletsProps}
					/>
				)
			case 'cards':
				const {
					content: cardsContent,
					props: cardsProps,
					propsItem: cardsPropsItem
				} = propsFor(cards)

				return (
					<InRow {...cardsProps}>
						{cardsContent.map((card, index) => {
							let cardContent = card
							if (typeof card === 'function') {
								cardContent = card()
							} else if (React.isValidElement(card)) {
								cardContent = card
							} else if (typeof card === 'string') {
								cardContent = card
							}

							return (
								<Card
									key={index}
									{
										// check if cardsPropsItem is array or object, if array use index, else use as is
										...(Array.isArray(cardsPropsItem)
											? cardsPropsItem[index] || {}
											: cardsPropsItem || {})
									}
								>
									{cardContent}
								</Card>
							)
						})}
					</InRow>
				)
			case 'list':
				const { content: listContent, props: listProps } =
					propsFor(list)

				return <CustomList dataSource={listContent} {...listProps} />
			case 'tabs':
				const {
					content: tabsContent,
					props: tabsProps,
					propsItem: tabsPropsItem
				} = propsFor(tabs)
				return (
					<Tabs
						{...tabsProps}
						items={tabsContent.map((element, index) => {
							const {
								key,
								tab: tabLabel,
								content: tabContent,
								...restTab
							} = element
							return {
								key: key || `tab-${index}`,
								label: tabLabel,
								children: (
									<div {...tabsPropsItem} {...restTab}>
										{tabContent}{' '}
									</div>
								)
							}
						})}
					/>
				)
			case 'collapse':
				const {
					content: collapseContent, // array of collapse items
					props: collapseProps,
					propsItem: collapsePropsItem // applied to each collapse item if exists
				} = propsFor(collapse)
				return (
					<Collapse
						{...collapseProps}
						items={collapseContent.map((item, index) => {
							const { key, label, children, ...restItem } = item
							return {
								key: key || index,
								label: label,
								children: children,
								...collapsePropsItem,
								...restItem
							}
						})}
					/>
				)
			case 'timeline':
				const { content: timelineContent, props: timelineProps } =
					propsFor(timeline)
				const { circleColor, ...restTimelineProps } = timelineProps

				return (
					<Timeline
						items={timelineContent.map((entry, index) => {
							// If entry is string, convert to object and assign to text
							if (typeof entry === 'string') {
								entry = { text: entry }
							}

							// Extract role and text
							const { role, text, ...restDialogEntry } = entry

							// Prepare text displayed with role if available
							let textDisplayed = ''
							if (role) {
								textDisplayed = (
									<>
										<strong>{role}:</strong> {text}
									</>
								)
							} else {
								textDisplayed = text
							}

							// Return timeline item
							return {
								key: index,
								color: circleColor(entry) || 'gray',
								children: textDisplayed,
								...restDialogEntry
							}
						})}
						{...restTimelineProps}
					/>
				)
			case 'steps': {
				// steps: array of {t, d}, optionally [stepsArray, stepsProps]
				const {
					content: stepsContent,
					props: stepsProps,
					propsItem: stepsPropsItem
				} = propsFor(steps)

				if (!Array.isArray(stepsContent) || stepsContent.length === 0)
					return null
				return (
					<Steps {...stepsProps}>
						{stepsContent.map((s, idx) => {
							const { t, d, ...restStep } = s
							return (
								<Steps.Step
									key={t || idx}
									title={t}
									description={d}
									{...restStep}
									{...stepsPropsItem}
								/>
							)
						})}
					</Steps>
				)
			}

			case 'buttons':
				const { content: buttonsContent, props: buttonsProps } =
					propsFor(buttons)
				return (
					<Space size="middle" wrap {...buttonsProps}>
						{buttonsContent.map((button, index) => {
							const { key, ...restButton } = button

							return (
								<CustomButton
									key={key || index}
									{...restButton}
								>
									{restButton.label || ''}
								</CustomButton>
							)
						})}
					</Space>
				)

			default:
				return null
		}
	}

	if (orderedElements.length > 0) {
		return (
			<Space
				className="presentation-component"
				direction="vertical"
				style={{ width: '100%' }}
				size="small"
				{...props}
			>
				{orderedElements.map((el) => (
					<React.Fragment key={`section-${el}`}>
						{renderElement(el)}
					</React.Fragment>
				))}
			</Space>
		)
	}

	return null
}

Presentation.propTypes = {
	// can be "Title" array or simple string
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.array // [content, props]
	]),
	// string, array of strings, or [content, props]
	description: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	// [string[] | node[], listProps?, itemProps?]
	bullets: PropTypes.array,
	// [bulletsArray, listProps?, itemProps?]
	cards: PropTypes.array,
	// [cardsArray, inRowProps?, cardPropsOrArray?]
	list: PropTypes.array,
	// [listArray, listProps?, itemProps?]
	tabs: PropTypes.array,
	// [tabsArray, tabsProps?, tabContentProps?]
	collapse: PropTypes.array,
	// [collapseArray, collapseProps?, collapseItemProps?]
	timeline: PropTypes.array,
	// [timelineArray, timelineProps?]
	steps: PropTypes.array,
	// [stepsArray, stepsProps?]
	buttons: PropTypes.array,
	// [buttonsArray, spaceProps?]
	image: PropTypes.array,
	// [src, imgProps?]
	video: PropTypes.array,
	// [src, videoProps?]
	order: PropTypes.array
	// order of sections
}

export default Presentation
